import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HiUsers } from 'react-icons/hi2';

const ArrowRightIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
);

interface QuickOptionsCardsProps {
    onGuidedFinderClick: () => void;
    onCompareLaptopsClick: () => void;
}

// Generate random concurrent users around 1 lakh (100,000)
const generateRandomUsers = () => {
    const base = 100000;
    const variance = 20000; // +/- 20k variation
    return Math.floor(base + (Math.random() * variance * 2) - variance);
};

export const QuickOptionsCards: React.FC<QuickOptionsCardsProps> = ({
    onGuidedFinderClick,
    onCompareLaptopsClick
}) => {
    const [concurrentUsers, setConcurrentUsers] = useState(generateRandomUsers());

    useEffect(() => {
        // Update user count every 10 seconds with smooth animation
        const interval = setInterval(() => {
            setConcurrentUsers(generateRandomUsers());
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    // Format number with commas
    const formatNumber = (num: number) => {
        return num.toLocaleString('en-IN');
    };

    return (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto mt-8 sm:mt-10 animate-fade-in">
            
            {/* Guided Finder Card */}
            <div
                onClick={onGuidedFinderClick}
                className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-2xl p-6 sm:p-8 shadow-custom hover:shadow-custom-lg transition-all duration-300 cursor-pointer group hover:-translate-y-1"
            >
                <div className="flex items-center justify-center w-12 h-12 bg-[var(--brand)]/10 rounded-xl mb-4 group-hover:bg-[#1e8a9e] transition-colors text-2xl">
                    💡
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[var(--text-1)] mb-3 font-serif">
                    Guided Finder
                </h3>
                <p className="text-[var(--text-2)] leading-relaxed mb-5 text-sm sm:text-base">
                    Answer a few quick questions about your needs, and get recommendations tailored just for you.
                </p>
                <button className="flex items-center gap-2 text-[var(--brand)] font-semibold hover:gap-3 transition-all group-hover:text-[var(--brand-hover)]">
                    Get Started <ArrowRightIcon />
                </button>
            </div>

            {/* Compare Laptops Card */}
            <div
                onClick={onCompareLaptopsClick}
                className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-2xl p-6 sm:p-8 shadow-custom hover:shadow-custom-lg transition-all duration-300 cursor-pointer group hover:-translate-y-1"
            >
                <div className="flex items-center justify-center w-12 h-12 bg-[var(--brand)]/10 rounded-xl mb-4 group-hover:bg-[#1e8a9e] transition-colors text-2xl">
                    ⚖️
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[var(--text-1)] mb-3 font-serif">
                    Compare Laptops
                </h3>
                <p className="text-[var(--text-2)] leading-relaxed mb-5 text-sm sm:text-base">
                    Add laptops side-by-side and see detailed specs, prices, and AI-powered comparisons.
                </p>
                <button className="flex items-center gap-2 text-[var(--brand)] font-semibold hover:gap-3 transition-all group-hover:text-[var(--brand-hover)]">
                    Compare Now <ArrowRightIcon />
                </button>
            </div>
        </div>

        {/* Concurrent Users Counter - Minimalist Design */}
        <motion.div 
            className="max-w-4xl mx-auto mt-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
        >
            <div className="flex items-center justify-center gap-2.5 text-[var(--text-3)]">
                {/* Live Pulse Indicator */}
                <div className="relative flex items-center">
                    <motion.div
                        className="w-2 h-2 bg-[var(--success)] rounded-full"
                        animate={{ opacity: [1, 0.4, 1] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <motion.div
                        className="absolute w-2 h-2 bg-[var(--success)] rounded-full"
                        animate={{ scale: [1, 2, 2], opacity: [0.5, 0, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    />
                </div>

                {/* User Count with Smooth Transition */}
                <span className="text-sm sm:text-base font-medium">
                    <motion.span
                        key={concurrentUsers}
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="font-semibold text-[var(--text-2)]"
                    >
                        {formatNumber(concurrentUsers)}
                    </motion.span>
                    {' '}users comparing laptops right now
                </span>
            </div>
        </motion.div>
        </>
    );
};
